// ┌─────────────────────────────────────────────────────────────────────────┐
// │ Client de l'ABM de programes de FITXER (dev-server middleware /__sp/).     │
// └─────────────────────────────────────────────────────────────────────────┘
//
// NOMÉS standalone (`npm run dev`): parla amb el plugin de Vite que llegeix/escriu
// standalonePrograms/{correction,calculation}/*.js a disc. La `meta` de cada programa
// es parseja al navegador amb el mateix Blob-import que el registre (remoteLoader).
import { buildProgramEntry, setStandalonePrograms, setStandaloneDeps, setModelDeps } from '@vft/config/standalonePrograms.js';
import { listForms, getFormDefinition, } from '@vft/api/definitionApi.js';
import { fetchProgramsByAliases, fetchCorrectionPrograms } from '@vft/api/programService.js';
import { getRuntimeConfig } from '@vft/config/runtimeConfig.js';

const BASE = '/__sp/programs';
const DEPS_BASE = '/__sp/deps';

// Blob-import d'una font per llegir-ne la `meta` i validar que exposa create(ctx).
async function parseSource(source) {
  const url = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
  try {
    const mod = await import(/* webpackIgnore: true */ /* @vite-ignore */ url);
    return {
      meta: (mod && mod.meta) || {},
      hasCreate: typeof (mod && (mod.default || mod.create)) === 'function',
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Llista els programes del disc + parseja la meta de cadascun.
// → [{ type, fileName, name, meta, source, hasCreate, error }]
export async function listPrograms() {
  const res = await fetch(BASE);
  if (!res.ok) throw new Error(`GET ${BASE} → ${res.status}`);
  const files = await res.json();
  const out = [];
  for (const f of files) {
    let meta = {};
    let hasCreate = false;
    let error = null;
    try {
      const parsed = await parseSource(f.source);
      meta = parsed.meta;
      hasCreate = parsed.hasCreate;
    } catch (e) {
      error = String((e && e.message) || e);
    }
    out.push({ type: f.type, fileName: f.fileName, name: meta.name || f.name, meta, source: f.source, hasCreate, error });
  }
  return out;
}

// Valida que una font compili com a mòdul i exposi el contracte create(ctx)→{ run }.
// Es fa servir abans de desar per no escriure a disc un programa trencat.
export async function validateProgramSource(source) {
  const url = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
  try {
    let mod;
    try {
      mod = await import(/* webpackIgnore: true */ /* @vite-ignore */ url);
    } catch (e) {
      return { ok: false, error: `no compila com a mòdul JS (${(e && e.message) || e})` };
    }
    const factory = mod && (mod.default || mod.create);
    if (typeof factory !== 'function') return { ok: false, error: 'falta «export default function create(ctx)»' };
    let calc;
    try {
      calc = factory({ deps: {}, fields: [], config: {} });
    } catch (e) {
      return { ok: false, error: `create(ctx) llança (${(e && e.message) || e})` };
    }
    if (!calc || typeof calc.run !== 'function') return { ok: false, error: 'create(ctx) no retorna { run(...) }' };
    return { ok: true };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function saveProgram(type, name, source) {
  const res = await fetch(`${BASE}/${type}/${encodeURIComponent(name)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `PUT → ${res.status}`);
  }
  return res.json();
}

export async function deleteProgram(type, name) {
  const res = await fetch(`${BASE}/${type}/${encodeURIComponent(name)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`DELETE → ${res.status}`);
  return res.json();
}

// ── Dependències (standalonePrograms/dependencies/) ────────────────────────
export async function listDeps() {
  const res = await fetch(DEPS_BASE);
  if (!res.ok) throw new Error(`GET ${DEPS_BASE} → ${res.status}`);
  return res.json(); // [{ name, depType, fileName, content }]
}

export async function saveDep(name, depType, content) {
  const res = await fetch(`${DEPS_BASE}/${encodeURIComponent(name)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ depType, content }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `PUT → ${res.status}`);
  }
  return res.json();
}

export async function deleteDep(name) {
  const res = await fetch(`${DEPS_BASE}/${encodeURIComponent(name)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`DELETE → ${res.status}`);
  return res.json();
}

async function refreshDepsRegistry() {
  const res = await fetch(DEPS_BASE);
  if (!res.ok) return;
  const files = await res.json();
  setStandaloneDeps(files.map((f) => ({ name: f.name, type: f.depType, content: f.content })));
}

// Àlies del MODEL: uneix els id de camp de tots els formularis actius (dedup +
// ordenats). Requereix connexió + token (barra CONFIG); un formulari que peti no
// atura la resta.
export async function fetchModelAliases() {
  const forms = await listForms();
  const lang = getRuntimeConfig().lang || 'ca_es';
  const set = new Set();
  for (const f of forms) {
    try {
      const def = await getFormDefinition(f.id, lang);
      for (const fld of def) { if (fld && fld.id) set.add(fld.id); }
    } catch { /* ignora el formulari que falla */ }
  }
  return [...set].sort();
}

// Refresca el REGISTRE en memòria des del disc, perquè els stores (passos 6 i 7)
// vegin els canvis a l'instant sense dependre d'una recàrrega HMR.
// Dependències del MODEL (catàleg): s'agreguen (úniques per nom, amb contingut) de
// tots els programes del model que tornen les tasks. Requereix connexió + token.
export async function fetchModelDeps() {
  const alias = await fetchProgramsByAliases([]);
  const corr = await fetchCorrectionPrograms();
  const byName = new Map();
  for (const p of [...alias, ...corr]) {
    for (const d of (p.dependencies || [])) {
      if (d && d.name && !byName.has(d.name)) byName.set(d.name, { name: d.name, type: d.type, content: d.content });
    }
  }
  return [...byName.values()];
}

// Programes del MODEL (catàleg): els que tornen les tasks aliasprogram (compute/
// preprocessing) + correctionprogram (correction), normalitzats a la MATEIXA forma
// de fila que els de disc (name + meta + code) + origin implícit 'model'. Read-only
// al panell (la font viu a la BD). Requereix connexió + token; dedup per id.
export async function fetchModelPrograms() {
  const alias = await fetchProgramsByAliases([]);
  const corr = await fetchCorrectionPrograms();
  const byKey = new Map();
  for (const p of [...corr, ...alias]) {
    const key = p.id || p.name;
    if (!key || byKey.has(key)) continue;
    const deps = (p.dependencies || []).map((d) => (d && d.name) || d).filter(Boolean);
    byKey.set(key, {
      id: p.id || null,
      name: p.name || key,
      code: p.code || '',
      meta: {
        name: p.name || key,
        type: p.type || 'compute',
        description: p.description || '',
        order: p.order != null ? p.order : 0,
        scope_mode: p.scopeMode || p.scope_mode || 'ALL',
        field_types: p.fieldTypes || p.field_types || [],
        aliases: Array.isArray(p.aliases) ? p.aliases : [],
        matchAll: p.matchAll === true || p.matchAll === 1 || p.matchAll === '1',
        dependencies: deps,
      },
    });
  }
  return [...byKey.values()];
}

let modelDepsLoaded = false;
// Carrega (una vegada) les dependències del model al registre i reconstrueix les
// entrades de programa de fitxer perquè resolguin també les del model. Silenciós si
// no hi ha token (es reintenta més tard).
export async function ensureModelDepsLoaded(force = false) {
  if (modelDepsLoaded && !force) return;
  try {
    setModelDeps(await fetchModelDeps());
    modelDepsLoaded = true;
    await refreshRegistry();
  } catch { /* sense token/connexió → deps del model no disponibles encara */ }
}

export async function refreshRegistry() {
  // Deps PRIMER: els programes les resolen per nom en construir-se.
  await refreshDepsRegistry();
  const res = await fetch(BASE);
  if (!res.ok) return;
  const files = await res.json();
  const entries = [];
  for (const f of files) {
    try {
      const { meta } = await parseSource(f.source);
      entries.push(buildProgramEntry(meta, f.source, f.type, f.name));
    } catch {
      /* fitxer amb error de sintaxi → s'omet del registre (però es veu a l'ABM) */
    }
  }
  setStandalonePrograms(entries.filter((e) => e.code));
}
